import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Plus, Search, Trash2, X, ArrowLeft, Edit3, AlertCircle } from 'lucide-react';
import DashboardShell from '@/components/layout/DashboardShell';
import { moduleBySlug } from '@/lib/modules';
import api from '@/lib/api';
import { formatApiError } from '@/lib/auth';

// Module-specific form schemas. Each schema = list of { name, label, type, required, options? }
const SCHEMAS = {
  events: [
    { name: 'name', label: 'Event name', type: 'text', required: true },
    { name: 'venue', label: 'Venue', type: 'text', required: true },
    { name: 'starts_at', label: 'Starts at', type: 'datetime-local', required: true },
    { name: 'capacity', label: 'Capacity', type: 'number' },
    { name: 'price', label: 'Ticket price (₦)', type: 'number' },
  ],
  engineering: [
    { name: 'title', label: 'Job title', type: 'text', required: true },
    { name: 'client', label: 'Client name', type: 'text', required: true },
    { name: 'category', label: 'Category', type: 'select', options: ['Computer repair', 'Electrical', 'Industrial maintenance', 'Solar & inverter', 'Installations', 'Audit'] },
    { name: 'address', label: 'Job address', type: 'text' },
    { name: 'estimate', label: 'Estimate (₦)', type: 'number' },
  ],
  financials: [
    { name: 'client', label: 'Client', type: 'text', required: true },
    { name: 'amount', label: 'Amount (₦)', type: 'number', required: true },
    { name: 'description', label: 'Description', type: 'textarea' },
    { name: 'due_date', label: 'Due date', type: 'date' },
    { name: 'status', label: 'Status', type: 'select', options: ['draft', 'sent', 'paid', 'overdue'] },
  ],
  'pathway-club': [
    { name: 'name', label: 'Learner name', type: 'text', required: true },
    { name: 'email', label: 'Email', type: 'email' },
    { name: 'track', label: 'Track', type: 'select', options: ['ICT Foundation', 'Web Development', 'Data & AI', 'Embedded systems', 'Mobile development'] },
    { name: 'cohort', label: 'Cohort', type: 'text' },
    { name: 'age', label: 'Age', type: 'number' },
  ],
  'corporate-hub': [
    { name: 'name', label: 'Lead name', type: 'text', required: true },
    { name: 'company', label: 'Company', type: 'text' },
    { name: 'email', label: 'Email', type: 'email' },
    { name: 'service_interest', label: 'Service interest', type: 'select', options: ['Consulting', 'Training', 'Speaking', 'Other'] },
    { name: 'notes', label: 'Notes', type: 'textarea' },
  ],
  'digital-laboratory': [
    { name: 'title', label: 'Project title', type: 'text', required: true },
    { name: 'kind', label: 'Project type', type: 'select', options: ['Video', 'Script', 'Image', 'Presentation'] },
    { name: 'description', label: 'Description', type: 'textarea' },
  ],
  'buy-sell': [
    { name: 'title', label: 'Listing title', type: 'text', required: true },
    { name: 'category', label: 'Category', type: 'select', options: ['Electronics', 'Fashion', 'Home', 'Vehicles', 'Services', 'Other'] },
    { name: 'price', label: 'Price (₦)', type: 'number', required: true },
    { name: 'condition', label: 'Condition', type: 'select', options: ['New', 'Like new', 'Used'] },
    { name: 'description', label: 'Description', type: 'textarea' },
  ],
  school: [
    { name: 'name', label: 'Student name', type: 'text', required: true },
    { name: 'class_arm', label: 'Class & arm', type: 'text', placeholder: 'e.g. JSS 2A' },
    { name: 'parent_name', label: 'Parent / guardian', type: 'text' },
    { name: 'parent_phone', label: 'Parent phone', type: 'tel' },
    { name: 'date_of_birth', label: 'Date of birth', type: 'date' },
    { name: 'gender', label: 'Gender', type: 'select', options: ['Female', 'Male'] },
  ],
  estate: [
    { name: 'unit_no', label: 'Unit / plot number', type: 'text', required: true },
    { name: 'unit_type', label: 'Unit type', type: 'select', options: ['Residential', 'Commercial', 'Mixed-use', 'Land'] },
    { name: 'owner_name', label: 'Owner / landlord', type: 'text' },
    { name: 'occupancy', label: 'Occupancy', type: 'select', options: ['Owner-occupied', 'Tenant', 'Vacant'] },
    { name: 'monthly_levy', label: 'Monthly levy (₦)', type: 'number' },
  ],
  office: [
    { name: 'name', label: 'Full name', type: 'text', required: true },
    { name: 'email', label: 'Email', type: 'email', required: true },
    { name: 'department', label: 'Department', type: 'text' },
    { name: 'role', label: 'Job title', type: 'text' },
    { name: 'start_date', label: 'Start date', type: 'date' },
    { name: 'salary', label: 'Salary (₦)', type: 'number' },
  ],
  'church-ngo': [
    { name: 'name', label: 'Member name', type: 'text', required: true },
    { name: 'phone', label: 'Phone', type: 'tel' },
    { name: 'ministry', label: 'Ministry / unit', type: 'text' },
    { name: 'membership_status', label: 'Status', type: 'select', options: ['Member', 'Visitor', 'Workers'] },
    { name: 'date_joined', label: 'Date joined', type: 'date' },
  ],
  hospital: [
    { name: 'name', label: 'Patient name', type: 'text', required: true },
    { name: 'date_of_birth', label: 'Date of birth', type: 'date' },
    { name: 'gender', label: 'Gender', type: 'select', options: ['Female', 'Male'] },
    { name: 'phone', label: 'Phone', type: 'tel' },
    { name: 'unit', label: 'Unit', type: 'select', options: ['OPD General', 'Cardiology', 'Pediatrics', 'OBGYN', 'Surgery', 'Lab', 'Pharmacy'] },
    { name: 'allergies', label: 'Known allergies', type: 'text' },
  ],
  retail: [
    { name: 'name', label: 'Product name', type: 'text', required: true },
    { name: 'sku', label: 'SKU', type: 'text', required: true },
    { name: 'category', label: 'Category', type: 'text' },
    { name: 'price', label: 'Price (₦)', type: 'number', required: true },
    { name: 'stock_qty', label: 'Stock on hand', type: 'number' },
    { name: 'reorder_level', label: 'Reorder level', type: 'number' },
  ],
  restaurant: [
    { name: 'name', label: 'Menu item', type: 'text', required: true },
    { name: 'category', label: 'Category', type: 'select', options: ['Starters', 'Mains', 'Sides', 'Drinks', 'Desserts'] },
    { name: 'price', label: 'Price (₦)', type: 'number', required: true },
    { name: 'is_available', label: 'Available', type: 'select', options: ['Yes', 'No'] },
    { name: 'description', label: 'Description', type: 'textarea' },
  ],
  government: [
    { name: 'subject', label: 'Case subject', type: 'text', required: true },
    { name: 'citizen_name', label: 'Citizen', type: 'text' },
    { name: 'citizen_phone', label: 'Citizen phone', type: 'tel' },
    { name: 'category', label: 'Category', type: 'select', options: ['Land', 'Permits', 'Welfare', 'Tax', 'Public services', 'Other'] },
    { name: 'priority', label: 'Priority', type: 'select', options: ['Low', 'Normal', 'High', 'Urgent'] },
    { name: 'description', label: 'Description', type: 'textarea' },
  ],
};

const TITLES = {
  events: 'Events', engineering: 'Work Orders', financials: 'Invoices', 'pathway-club': 'Learners',
  'corporate-hub': 'Leads', 'digital-laboratory': 'Projects', 'buy-sell': 'Listings', school: 'Students',
  estate: 'Properties', office: 'Employees', 'church-ngo': 'Members', hospital: 'Patients',
  retail: 'Products', restaurant: 'Menu Items', government: 'Cases',
};

export default function ModuleRecords() {
  const { slug } = useParams();
  const module = moduleBySlug(slug);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/m/${slug}/records`, { params: { q: search || undefined, limit: 100 } });
      setItems(data.items || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug, search]);

  if (!module) return <Navigate to="/control-panel" replace />;
  const accent = module.accent;
  const schema = SCHEMAS[slug] || [];
  const title = TITLES[slug] || 'Records';

  const remove = async (rid) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      await api.delete(`/m/${slug}/records/${rid}`);
      load();
    } catch (e) { alert(formatApiError(e)); }
  };

  const titleField = schema[0]?.name || 'name';

  return (
    <DashboardShell module={module} sectionTitle={title}>
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div>
          <Link to={`/dashboard/${slug}`} className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-navy mb-2 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to dashboard
          </Link>
          <h1 className="font-display font-bold text-3xl tracking-tight text-navy">{title}</h1>
          <p className="text-neutral-600 mt-1 text-sm">Manage all {title.toLowerCase()} for this organization.</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md font-medium text-white text-sm transition-all hover:-translate-y-px" style={{ background: accent }} data-testid="add-record-btn">
          <Plus className="w-4 h-4" /> New {title.replace(/s$/, '')}
        </button>
      </div>

      <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-neutral-200 flex items-center gap-2">
          <Search className="w-4 h-4 text-neutral-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${title.toLowerCase()}…`} className="flex-1 outline-none text-sm" data-testid="records-search" />
          <span className="text-xs text-neutral-500">{items.length} record{items.length === 1 ? '' : 's'}</span>
        </div>

        {loading ? (
          <div className="p-12 text-center text-neutral-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center">
            <div className="mx-auto h-14 w-14 rounded-md flex items-center justify-center mb-4" style={{ background: `${accent}10`, color: accent }}>
              <Plus className="w-7 h-7" />
            </div>
            <h4 className="font-display font-semibold text-lg text-navy mb-1">No records yet</h4>
            <p className="text-sm text-neutral-500 mb-5">Create the first {title.toLowerCase().replace(/s$/, '')} to get started.</p>
            <button onClick={() => { setEditing(null); setShowForm(true); }} className="inline-flex items-center gap-2 text-sm font-medium" style={{ color: accent }}>
              <Plus className="w-4 h-4" /> Add {title.replace(/s$/, '')}
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-200 bg-neutral-50/50">
                <th className="text-left text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-5 py-3">Reference</th>
                {schema.slice(0, 3).map((f) => (
                  <th key={f.name} className="text-left text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-3 py-3">{f.label}</th>
                ))}
                <th className="text-left text-[11px] uppercase tracking-wider text-neutral-500 font-semibold px-3 py-3">Created</th>
                <th className="px-2 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-neutral-100 hover:bg-neutral-50/60 transition-colors" data-testid={`record-row-${r.id}`}>
                  <td className="px-5 py-3.5 font-mono text-xs text-neutral-600">{r.number || r.case_no || r.patient_no || r.admission_no || r.sku || r.id.slice(0, 8)}</td>
                  {schema.slice(0, 3).map((f) => (
                    <td key={f.name} className="px-3 py-3.5 text-sm text-navy">
                      {f.name === titleField ? <span className="font-medium">{r[f.name] || '—'}</span> : (r[f.name] ?? '—')}
                    </td>
                  ))}
                  <td className="px-3 py-3.5 text-xs text-neutral-500 font-mono">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="px-2 py-3.5">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => { setEditing(r); setShowForm(true); }} className="p-1.5 rounded hover:bg-neutral-100 text-neutral-500" data-testid={`edit-${r.id}`}>
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => remove(r.id)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500" data-testid={`delete-${r.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <RecordForm
          slug={slug}
          schema={schema}
          accent={accent}
          editing={editing}
          title={title.replace(/s$/, '')}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </DashboardShell>
  );
}

function RecordForm({ slug, schema, accent, editing, title, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    const init = {};
    schema.forEach((f) => { init[f.name] = editing?.[f.name] ?? ''; });
    return init;
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setError(''); setBusy(true);
    try {
      const payload = {};
      schema.forEach((f) => {
        let v = form[f.name];
        if (f.type === 'number' && v !== '') v = Number(v);
        if (v === '' || v === undefined || v === null) return;
        payload[f.name] = v;
      });
      if (editing) {
        await api.patch(`/m/${slug}/records/${editing.id}`, payload);
      } else {
        await api.post(`/m/${slug}/records`, payload);
      }
      onSaved();
    } catch (err) { setError(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-6" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-t-md sm:rounded-md shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto" data-testid="record-form-modal">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="label-tag mb-1" style={{ color: accent }}>{editing ? 'Edit' : 'New'}</div>
            <h3 className="font-display font-semibold text-xl text-navy">{title}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {schema.map((f) => (
            <label key={f.name} className="block">
              <span className="block text-[11px] uppercase tracking-wider text-neutral-600 font-semibold mb-1.5">
                {f.label}{f.required && <span className="text-rose-500">*</span>}
              </span>
              {f.type === 'textarea' ? (
                <textarea value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} required={f.required} rows={3} className="w-full px-3 py-2 border border-neutral-200 rounded-md outline-none focus:border-navy focus:ring-2 focus:ring-navy/10" />
              ) : f.type === 'select' ? (
                <select value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} required={f.required} className="w-full px-3 py-2 border border-neutral-200 rounded-md outline-none bg-white">
                  <option value="">Select…</option>
                  {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input
                  type={f.type}
                  value={form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  required={f.required}
                  placeholder={f.placeholder}
                  className="w-full px-3 py-2 border border-neutral-200 rounded-md outline-none focus:border-navy focus:ring-2 focus:ring-navy/10"
                />
              )}
            </label>
          ))}

          {error && (
            <div className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-200 rounded-md text-sm text-rose-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-neutral-100">
            <button type="button" onClick={onClose} className="btn-outline text-sm">Cancel</button>
            <button
              type="submit"
              disabled={busy}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md font-medium text-white text-sm transition-all hover:-translate-y-px disabled:opacity-60"
              style={{ background: accent }}
              data-testid="submit-record"
            >
              {busy ? 'Saving…' : (editing ? 'Save changes' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
